import React, { useMemo } from 'react'
import { TextInput } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import { formatMoney } from '../../../utils/formatMoney'
import type { OpsCashCountValue, OpsTemplateFieldCashCountField } from '../../../types/ops'

export interface CashCountFieldInputProps {
  field: OpsTemplateFieldCashCountField
  value: unknown
  onChange: (nextValue: OpsCashCountValue) => void
  inputId?: string
  currency?: string | null
  previousClosingCount?: number | null
}

export const CashCountFieldInput: React.FC<CashCountFieldInputProps> = ({
  field,
  value,
  onChange,
  inputId,
  currency,
  previousClosingCount,
}) => {
  const { t } = useNls()
  const baseId = inputId ?? `ops-cash-${field.key}`

  const cashVal: OpsCashCountValue = useMemo(() => {
    if (value && typeof value === 'object') {
      const v = value as Record<string, unknown>
      return {
        counts: (v.counts && typeof v.counts === 'object' ? (v.counts as Record<string, number>) : {}) ?? {},
        looseMinor: typeof v.looseMinor === 'number' ? v.looseMinor : 0,
        expectedMinor: typeof v.expectedMinor === 'number' ? v.expectedMinor : 0,
        mismatchReason: typeof v.mismatchReason === 'string' ? v.mismatchReason : '',
        countedMinor: typeof v.countedMinor === 'number' ? v.countedMinor : 0,
        differenceMinor: typeof v.differenceMinor === 'number' ? v.differenceMinor : 0,
      }
    }
    return {
      counts: {},
      looseMinor: 0,
      expectedMinor: 0,
      mismatchReason: '',
      countedMinor: 0,
      differenceMinor: 0,
    }
  }, [value])

  const sortedDenominations = useMemo(() => {
    const list = Array.isArray(field.denominations) ? [...field.denominations] : []
    return list.sort((a, b) => b - a)
  }, [field.denominations])

  // Recalculate totals
  const handleCountsChange = (denom: number, countStr: string) => {
    const qty = countStr.trim() === '' ? 0 : Math.max(0, Math.min(100000, Number.parseInt(countStr, 10) || 0))
    const nextCounts = { ...cashVal.counts, [denom]: qty }
    recalculate(nextCounts, cashVal.looseMinor ?? 0, cashVal.expectedMinor ?? 0, cashVal.mismatchReason)
  }

  const handleLooseChange = (looseStr: string) => {
    const loose = looseStr.trim() === '' ? 0 : Math.max(0, Number.parseInt(looseStr, 10) || 0)
    recalculate(cashVal.counts, loose, cashVal.expectedMinor ?? 0, cashVal.mismatchReason)
  }

  const handleExpectedChange = (expectedStr: string) => {
    const exp = expectedStr.trim() === '' ? 0 : Math.max(0, Number.parseInt(expectedStr, 10) || 0)
    recalculate(cashVal.counts, cashVal.looseMinor ?? 0, exp, cashVal.mismatchReason)
  }

  const handleReasonChange = (reason: string) => {
    onChange({
      ...cashVal,
      mismatchReason: reason,
    })
  }

  const recalculate = (
    counts: Record<string | number, number>,
    loose: number,
    expected: number,
    mismatchReason?: string,
  ) => {
    let counted = loose
    for (const [denomStr, qty] of Object.entries(counts)) {
      const d = Number(denomStr)
      if (Number.isFinite(d) && Number.isFinite(qty) && qty > 0) {
        counted += Math.round(d * qty)
      }
    }
    const diff = counted - expected
    onChange({
      counts,
      looseMinor: loose,
      expectedMinor: expected,
      countedMinor: counted,
      differenceMinor: diff,
      mismatchReason: mismatchReason ?? cashVal.mismatchReason,
    })
  }

  const countedMinor = cashVal.countedMinor ?? 0
  const expectedMinor = cashVal.expectedMinor ?? 0
  const differenceMinor = cashVal.differenceMinor ?? 0
  const tolerance = field.toleranceMinor ?? 0
  const isOverTolerance = Math.abs(differenceMinor) > tolerance

  return (
    <div className="opsRegistersCashTable" id={baseId}>
      {field.carryFloat && previousClosingCount != null && (
        <div className="opsRegistersCashOpeningFloat">
          {t('NLSOpsRegisterCashCountOpeningFloat', {
            amount: formatMoney(previousClosingCount, currency),
          })}
        </div>
      )}

      {/* Header */}
      <div className="opsRegistersCashRow opsRegistersCashHeader">
        <span>{t('NLSOpsRegisterCashCountNoteCoinCol')}</span>
        <span>{t('NLSOpsRegisterCashCountQtyCol')}</span>
        <span className="opsRegistersFiguresRight">{t('NLSOpsRegisterCashCountAmountCol')}</span>
      </div>

      {/* Denomination rows */}
      {sortedDenominations.map((denom) => {
        const qty = cashVal.counts[denom] ?? 0
        const lineAmount = qty * denom
        const rowInputId = `${baseId}-denom-${denom}`
        return (
          <div key={denom} className="opsRegistersCashRow">
            <label htmlFor={rowInputId} className="opsRegistersFigures">
              {formatMoney(denom, currency)}
            </label>
            <TextInput
              id={rowInputId}
              type="number"
              aria-label={`${formatMoney(denom, currency)} ${t('NLSOpsRegisterCashCountQtyCol')}`}
              value={qty > 0 ? String(qty) : ''}
              onChange={(raw) => handleCountsChange(denom, raw)}
            />
            <span className="opsRegistersFigures opsRegistersFiguresRight">
              {formatMoney(lineAmount, currency)}
            </span>
          </div>
        )
      })}

      {/* Loose cash row */}
      <div className="opsRegistersCashRow">
        <label htmlFor={`${baseId}-loose`} className="opsRegistersFieldLabel">
          {t('NLSOpsRegisterCashCountLooseLabel')}
        </label>
        <TextInput
          id={`${baseId}-loose`}
          type="number"
          aria-label={t('NLSOpsRegisterCashCountLooseLabel')}
          value={cashVal.looseMinor ? String(cashVal.looseMinor) : ''}
          onChange={handleLooseChange}
        />
        <span className="opsRegistersFigures opsRegistersFiguresRight">
          {formatMoney(cashVal.looseMinor ?? 0, currency)}
        </span>
      </div>

      {/* Counted total */}
      <div className="opsRegistersCashTotalRow">
        <span>{t('NLSOpsRegisterCashCountCountedTotal')}</span>
        <span className="opsRegistersFigures">{formatMoney(countedMinor, currency)}</span>
      </div>

      {/* Expected total row */}
      <div className="opsRegistersCashRow">
        <label htmlFor={`${baseId}-expected`} className="opsRegistersFieldLabel">
          {t('NLSOpsRegisterCashCountExpectedLabel')}
        </label>
        <TextInput
          id={`${baseId}-expected`}
          type="number"
          aria-label={t('NLSOpsRegisterCashCountExpectedLabel')}
          value={expectedMinor ? String(expectedMinor) : ''}
          onChange={handleExpectedChange}
        />
        <span className="opsRegistersFigures opsRegistersFiguresRight">
          {formatMoney(expectedMinor, currency)}
        </span>
      </div>

      {/* Difference */}
      <div className="opsRegistersCashTotalRow">
        <span>{t('NLSOpsRegisterCashCountDifferenceTotal')}</span>
        <span
          className={`opsRegistersFigures ${differenceMinor < 0 || isOverTolerance ? 'opsRegistersCashDiffBad' : ''}`}
        >
          {differenceMinor < 0 ? `−${formatMoney(Math.abs(differenceMinor), currency)}` : formatMoney(differenceMinor, currency)}
        </span>
      </div>

      {/* Mismatch reason when over tolerance */}
      {isOverTolerance && (
        <div className="opsRegistersMismatchBox">
          <label htmlFor={`${baseId}-mismatch-reason`} className="opsRegistersFieldLabel">
            {t('NLSOpsRegisterCashCountMismatchReasonLabel')}
          </label>
          <TextInput
            id={`${baseId}-mismatch-reason`}
            aria-label={t('NLSOpsRegisterCashCountMismatchReasonLabel')}
            placeholder={t('NLSOpsRegisterCashCountMismatchReasonPlaceholder')}
            value={cashVal.mismatchReason ?? ''}
            onChange={handleReasonChange}
          />
        </div>
      )}
    </div>
  )
}
